import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/*
 * Schéma systému v hero: webová aplikácia → backend → databáza v ráme prevádzky. Dekoratívna (aria-hidden),
 * lebo opakuje sekciu Služby. Zobrazuje sa od 1024 px. Kreslenie pri načítaní je v index.css (.system).
 * Tvrdenie „SLA 24/7“ je na jednom mieste: prázdny reťazec ho z rámu odstráni.
 */
const SLA_CLAIM = 'SLA 24/7'

const ICON_WEB = <path d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
const ICON_API = <path d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Z" />
const ICON_DB = <path d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />

// Oneskorenie prvku v sekvencii kreslenia (ms od začiatku)
const delay = (ms: number) => ({ '--hd': `${ms}ms` }) as CSSProperties

function Node({ ms, icon, label, tech }: { ms: number; icon: ReactNode; label: string; tech: string }) {
  return (
    <div
      className="node flex items-center gap-4 rounded-xl border border-white/15 p-4"
      style={{ ...delay(ms), background: 'linear-gradient(145deg, rgba(41, 60, 110, 0.92), rgba(28, 43, 82, 0.88))' }}
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/10 text-indigo-300">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          {icon}
        </svg>
      </span>
      <span>
        <span className="block text-sm font-semibold text-white">{label}</span>
        <span className="font-mono text-xs text-slate-300/80">{tech}</span>
      </span>
    </div>
  )
}

function Link({ ms }: { ms: number }) {
  return (
    <div className="link-v" style={delay(ms)}>
      <span className="req" />
    </div>
  )
}

export default function SystemDiagram() {
  const { t } = useTranslation()
  return (
    <div className="system hidden lg:block text-left" aria-hidden="true">
      <div className="system-frame relative rounded-2xl p-6" style={delay(640)}>
        <div className="tag mb-4 font-mono text-xs text-indigo-300">
          {t('hero.diagram_ops')}
          {SLA_CLAIM ? ` · ${SLA_CLAIM}` : null}
        </div>
        <Node ms={0} icon={ICON_WEB} label={t('hero.diagram_web')} tech="React · TypeScript" />
        <Link ms={170} />
        <Node ms={280} icon={ICON_API} label={t('hero.diagram_api')} tech="Java · Spring Boot" />
        <Link ms={450} />
        <Node ms={560} icon={ICON_DB} label={t('hero.diagram_db')} tech="PostgreSQL · Oracle" />
      </div>
    </div>
  )
}
